import { i18n } from "@/boot/i18n";
import * as types from "./mutationTypes";
import ErrorHandler from "@/tools/errorHandler";
import UserService from "@/services/user";
import { EMAILS, REQUIRED } from "../validator/methods";

export default {
  initListState({ commit }) {
    commit(types.SET_USERS, []);
    commit(types.SET_TOTAL_USERS, 0);
  },
  clearListState({ dispatch }) {
    dispatch("clearUsers");
    dispatch("team/clearTeams", null, { root: true });
  },
  initNewState({ commit, dispatch }) {
    commit(types.SET_INVITE, { emails: [], team_uuid: null });
    dispatch("validator/clearValidator", null, { root: true });
    const validator = validators();
    dispatch("validator/addValidator", validator.teamValidator, { root: true });
    dispatch("validator/addValidator", validator.emailsValidator, { root: true });
  },
  clearNewState({ commit, dispatch }) {
    commit(types.CLEAR_INVITE);
    dispatch("validator/clearValidator", null, { root: true });
  },
  setTrackUsersSuccess({ dispatch }, users) {
    dispatch("setTrackUsers", users);
  },
  setUsersSuccess({ commit }, { users, total }) {
    commit(types.SET_USERS, users);
    commit(types.SET_TOTAL_USERS, total);
  },
  async getOne({ dispatch, rootState }, uuid) {
    try {
      const response = await UserService.getOne(rootState.organization.organization.uuid, uuid);
      return response.data.data;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async createAccessRequest({ dispatch }, { uuid, reason }) {
    try {
      await UserService.createAccessRequest(uuid, reason);
      dispatch("ui/showToastSuccess", i18n.t("organization.messages.request_access_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getTrackUsers({ dispatch }) {
    try {
      const response = await UserService.getTrackUsers();
      if (response.data.data.users) {
        dispatch("setTrackUsersSuccess", response.data.data.users);
      } else {
        dispatch("setTrackUsersSuccess", []);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAll({ rootState, dispatch }, pagination) {
    try {
      const response = await UserService.getAll(rootState.organization.organization.uuid, pagination);
      if (response.data.data.users) {
        dispatch("setUsersSuccess", {
          users: response.data.data.users,
          total: response.data.data.total_rows
        });
      } else {
        dispatch("setUsersSuccess", { users: [], total: 0 });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllBlock({ rootState, dispatch }, pagination) {
    try {
      const response = await UserService.getAllBlock(rootState.organization.organization.uuid, pagination);
      if (response.data.data.users) {
        dispatch("setUsersSuccess", {
          users: response.data.data.users,
          total: response.data.data.total_rows
        });
      } else {
        dispatch("setUsersSuccess", { users: [], total: 0 });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllRequested({ rootState, dispatch }, pagination) {
    try {
      const response = await UserService.getAllRequested(rootState.organization.organization.uuid, pagination);
      if (response.data.data.access_requests) {
        dispatch("setUsersSuccess", {
          users: response.data.data.access_requests,
          total: response.data.data.total_rows
        });
      } else {
        dispatch("setUsersSuccess", { users: [], total: 0 });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllInvite({ rootState, dispatch }, pagination) {
    try {
      const response = await UserService.getAllInvite(rootState.organization.organization.uuid, pagination);
      if (response.data.data.invite) {
        dispatch("setUsersSuccess", {
          users: response.data.data.invite,
          total: response.data.data.total_rows
        });
      } else {
        dispatch("setUsersSuccess", { users: [], total: 0 });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async block({ rootState, dispatch }, userUUID) {
    try {
      await UserService.block(rootState.organization.organization.uuid, userUUID);
      dispatch("ui/showToastSuccess", i18n.t("user.messages.user_block_success"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async unblock({ rootState, dispatch }, userUUID) {
    try {
      await UserService.unblock(rootState.organization.organization.uuid, userUUID);
      dispatch("ui/showToastSuccess", i18n.t("user.messages.user_unblock_success"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async delete({ rootState, dispatch }, userUUID) {
    try {
      await UserService.delete(rootState.organization.organization.uuid, userUUID);
      dispatch("ui/showToastSuccess", i18n.t("user.messages.remove_success"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async updateAccessRequest({ rootState, dispatch }, { accessRequestUUID, isApproved, teamUUID }) {
    try {
      await UserService.updateAccessRequest(
        rootState.organization.organization.uuid,
        accessRequestUUID,
        isApproved,
        teamUUID
      );
      const message = isApproved
        ? i18n.t("user.messages.user_approved_success")
        : i18n.t("user.messages.user_rejected_success");

      dispatch("ui/showToastSuccess", message, { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async createInvite({ rootState, dispatch }, { email, teamUUID }) {
    try {
      await UserService.createInvite(rootState.organization.organization.uuid, email, teamUUID);
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error, [email]), { root: true });
      return false;
    }
  },
  async createMultipleInvites({ rootState, dispatch, state }) {
    const valid = await dispatch("validate");
    if (!valid) {
      return false;
    }
    const emailsInvited = [];
    const emails = state.invite.emails;
    for (let i = 0; i < emails.length; i++) {
      const email = emails[i];
      try {
        await UserService.createInvite(rootState.organization.organization.uuid, email, state.invite.team_uuid);
        emailsInvited.push(email);
      } catch (error) {
        dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error, [email]), { root: true });
      }
    }
    if (emailsInvited.length) {
      dispatch("ui/showToastSuccess", emailsInvited.join(", ") + " " + i18n.t("user.messages.user_invite_success"), {
        root: true
      });
    }
    return true;
  },
  async deleteInvite({ rootState, dispatch }, userUUID) {
    try {
      await UserService.deleteInvite(rootState.organization.organization.uuid, userUUID);
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  setTrackUsers({ commit }, users) {
    commit(types.SET_TRACK_USERS, users);
  },
  clearTrackUsers({ commit }) {
    commit(types.CLEAR_TRACK_USERS);
  },
  clearUsers({ commit }) {
    commit(types.CLEAR_USERS);
    commit(types.CLEAR_TOTAL_USERS);
  },
  async setEmails({ commit, dispatch }, emails) {
    const isValid = await dispatch("validator/validate", { field: "emails", value: emails }, { root: true });
    if (isValid) {
      commit(types.SET_EMAILS, emails);
    }
  },
  setTeam({ commit, dispatch }, teamUUID) {
    commit(types.SET_TEAM, teamUUID);
    dispatch("validator/checkValidation", { field: "team_uuid", value: teamUUID }, { root: true });
  },
  validate({ rootState, state, dispatch }) {
    dispatch("validator/validate", { field: "team_uuid", value: state.invite.team_uuid }, { root: true });
    dispatch("validator/validate", { field: "emails", value: state.invite.emails }, { root: true });
    return rootState.validator.errors.length === 0;
  }
};

const validators = () => {
  return {
    teamValidator: {
      field: "team_uuid",
      validations: [{ method: REQUIRED, message: i18n.t("general.rules.required") }],
      meta: { custom: false }
    },
    emailsValidator: {
      field: "emails",
      validations: [{ method: EMAILS, message: i18n.t("general.rules.email") }],
      meta: { custom: false }
    }
  };
};
